import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);

  console.log('AuthContext: Initializing', { token: !!token, user: !!user, loading });

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      console.log('AuthContext: Set Authorization header');
    } else {
      delete axios.defaults.headers.common['Authorization'];
      console.log('AuthContext: Removed Authorization header');
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('AuthContext: Checking authentication', { token: !!token });
      if (token) {
        try {
          console.log('AuthContext: Fetching user data from /users/me/');
          const response = await axios.get(`users/me/`);
          console.log('AuthContext: User data received', response.data);
          console.log('AuthContext: User data structure', {
            id: response.data.id,
            username: response.data.username,
            email: response.data.email,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            user_type: response.data.user_type,
            role: response.data.role,
            is_staff: response.data.is_staff,
            is_superuser: response.data.is_superuser
          });
          setUser(response.data);
        } catch (error) {
          console.error('AuthContext: Auth check failed:', error);
          logout();
        }
      } else {
        console.log('AuthContext: No token found');
      }
      console.log('AuthContext: Setting loading to false');
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    console.log('AuthContext: Login attempt', { username });
    try {
      const response = await axios.post(`users/login/`, {
        username,
        password
      });
      
      const { token: newToken, user: userData, user_type } = response.data;
      console.log('AuthContext: Login successful', { userData, user_type });
      
      setToken(newToken);
      setUser({ ...userData, user_type });
      localStorage.setItem('authToken', newToken);
      
      return { success: true, user_type };
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      // Convert error object to string
      const errorMessage = error.response?.data?.error || 
                          (typeof error.response?.data === 'object' ? 
                           Object.values(error.response.data).join(', ') : 
                           'Login failed');
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    console.log('AuthContext: Registration attempt', { username: userData.username });
    try {
      const response = await axios.post(`users/register/`, userData);
      
      const { token: newToken, user: userInfo } = response.data;
      console.log('AuthContext: Registration successful', { userInfo });
      
      setToken(newToken);
      setUser({ ...userInfo, user_type: 'patient' }); // Default role is patient
      localStorage.setItem('authToken', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Registration failed:', error);
      // Convert error object to string
      const errorMessage = error.response?.data?.error || 
                          (typeof error.response?.data === 'object' ? 
                           Object.values(error.response.data).join(', ') : 
                           'Registration failed');
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    console.log('AuthContext: Logging out');
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAuthenticated = () => {
    const authenticated = !!token && !!user;
    console.log('AuthContext: isAuthenticated called', { token: !!token, user: !!user, authenticated });
    return authenticated;
  };

  const getUserType = () => {
    const userType = user?.user_type || null;
    console.log('AuthContext: getUserType called', { userType, user });
    return userType;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated,
    getUserType
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 